<template>
  <div class="quick-options-card" :class="cardClass">
    <!-- 标题栏 -->
    <div class="quick-options-card__titlebar">
      <span class="quick-options-card__titlebar-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <path d="M8 12h8M12 8v8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="quick-options-card__titlebar-text">{{ card.question }}</span>
      <span class="quick-options-card__status-badge">{{ statusBadge }}</span>
    </div>

    <!-- 选项列表（pending 状态且本地未选且未提交中时显示） -->
    <div v-if="card.status === 'pending' && localSelected === null && !submitting" class="quick-options-card__options">
      <button
        v-for="opt in card.options"
        :key="opt.value"
        class="quick-options-card__option"
        @click="onSelect(opt)"
      >
        <span class="quick-options-card__option-label">{{ opt.label }}</span>
        <span v-if="opt.description" class="quick-options-card__option-desc">{{ opt.description }}</span>
      </button>
    </div>

    <!-- 提交中 loading 状态 -->
    <div v-else-if="submitting && card.status === 'pending'" class="quick-options-card__loading">
      <span class="quick-options-card__loading-spinner"></span>
      <span class="quick-options-card__loading-text">AI 正在处理，请稍候…</span>
    </div>

    <!-- 已选中状态 -->
    <div v-else-if="card.status === 'answered' || localSelected !== null" class="quick-options-card__answered">
      <span class="quick-options-card__answered-icon">✓</span>
      <span class="quick-options-card__answered-text">{{ answeredLabel }}</span>
    </div>

    <!-- 已过期 / 已取消 -->
    <div v-else class="quick-options-card__answered quick-options-card__answered--expired">
      <span class="quick-options-card__answered-text">已超时取消</span>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';

export default {
  name: 'QuickOptionsCard',
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  emits: ['action'],
  setup(props, { emit }) {
    const localSelected = ref(null); // null | { label, value }
    const submitting = ref(false);
    const pendingOpt = ref(null); // 点击后暂存选中项，等 answered 后再赋给 localSelected

    const cardClass = computed(() => {
      const effectiveStatus = localSelected.value !== null ? 'answered' : props.card.status;
      return `quick-options-card--${effectiveStatus}`;
    });

    const statusBadge = computed(() => {
      const effectiveStatus = localSelected.value !== null ? 'answered' : props.card.status;
      const map = { pending: '请选择', answered: '已选择', expired: '已超时' };
      return map[effectiveStatus] || '';
    });

    const answeredLabel = computed(() => {
      if (localSelected.value) return localSelected.value.label;
      if (props.card.selectedValue) {
        const opt = (props.card.options || []).find((o) => o.value === props.card.selectedValue);
        return opt ? opt.label : props.card.selectedValue;
      }
      return '';
    });

    // 监听 card.status 变化：当服务端回调 answered 时，才把暂存的选项赋给 localSelected
    watch(
      () => props.card.status,
      (newStatus) => {
        if (newStatus === 'answered' && pendingOpt.value) {
          localSelected.value = pendingOpt.value;
          pendingOpt.value = null;
          submitting.value = false;
        }
      }
    );

    function onSelect(opt) {
      if (submitting.value) return;
      submitting.value = true;
      pendingOpt.value = opt; // 暂存，先显示 loading
      emit('action', props.card.cardId, 'select', opt.value);
    }

    return { localSelected, submitting, cardClass, statusBadge, answeredLabel, onSelect };
  },
};
</script>

<style scoped>
.quick-options-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #d8e3f5;
  overflow: hidden;
  margin: 4px 0;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.08);
  width: 100%;
}
.quick-options-card--answered { border-color: #b7ebc8; box-shadow: 0 2px 8px rgba(7, 193, 96, 0.08); }
.quick-options-card--expired { border-color: #e8ecf0; box-shadow: none; opacity: 0.75; }

.quick-options-card__titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  border-bottom: 1px solid #d8e3f5;
}
.quick-options-card__titlebar-icon {
  display: flex;
  align-items: center;
  color: #0052D9;
  flex-shrink: 0;
}
.quick-options-card__titlebar-text {
  font-size: 13px;
  font-weight: 600;
  color: #1a202c;
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
}
.quick-options-card__status-badge {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #e8f0fe;
  color: #0052D9;
  font-weight: 500;
  white-space: nowrap;
}
.quick-options-card--answered .quick-options-card__status-badge { background: #e8f8ef; color: #07c160; }
.quick-options-card--expired .quick-options-card__status-badge { background: #f5f7fa; color: #86909c; }

.quick-options-card__options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 14px 12px;
}
.quick-options-card__option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #f8faff;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  width: 100%;
}
.quick-options-card__option:hover:not(:disabled) {
  border-color: #0052D9;
  background: #f0f4ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 82, 217, 0.12);
}
.quick-options-card__option:disabled { opacity: 0.6; cursor: not-allowed; }
.quick-options-card__option-label { font-size: 13px; font-weight: 500; color: #1a202c; }
.quick-options-card__option-desc { font-size: 12px; color: #86909c; }

.quick-options-card__answered {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px 12px;
}
.quick-options-card__answered--expired { opacity: 0.6; }
.quick-options-card__answered-icon { font-size: 14px; color: #07c160; }
.quick-options-card__answered-text { font-size: 13px; color: #4a5568; }

.quick-options-card__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
}
.quick-options-card__loading-text {
  font-size: 13px;
  color: #86909c;
}
.quick-options-card__loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #d8e3f5;
  border-top-color: #0052D9;
  border-radius: 50%;
  animation: qs-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes qs-spin {
  to { transform: rotate(360deg); }
}
</style>
